import React from "react";
import styled from "styled-components";
import { RouteComponentProps } from "react-router";
import { apiClient } from "../../App";
import IMessageSequence from "../../../../shared/IMessageSequence";
import MessageRow from "../../components/MessageRow";
import RecordingStatusBar from "../../components/RecordingStatusBar";
import ActiveFilter from "../../components/ActiveFilter";
import Help from "../../components/Help";
import clipboardCopy from "clipboard-copy";
import { FlashControl } from "../../components/FlashMessage";
import { isOptionsMessage } from "../../SubjectUtils";
import { replayStorage } from "../../storage/ReplayStorage";
import SequenceDiagram from "../../components/SequenceDiagram";
import { libraryStorage } from "../../storage/LibraryStorage";

type Shortcuts = "o" | "t" | "c" | "m" | "r" | "s" | "f";

const MODIFIER_KEYS = ["Meta", "Control", "Ctrl"];

interface MatchParams {
  id: string;
  reqId: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

const StyledMessageSequence = styled.div`
  position: relative;

  .messages {
    height: 100vh;
    overflow-y: scroll;
    padding-bottom: 300px;
  }

  .seq-diagram {
    padding: 4rem 2rem;
  }
`;

interface State {
  messageSequence: null | IMessageSequence;
  messageInFocus: null | any;
  activeTransaction: null | string;
  activeFilters: Shortcuts[];
  showSequenceDiagram: boolean;
}

interface IFilter {
  shortcut: Shortcuts;
  help: string;
  activeText?: string;
  handler?: () => void;
  flashMessage?: string;
}

class MessageSequence extends React.Component<Props> {
  isCommandOrControlKeyActive = false;

  state: State = {
    messageSequence: null,
    messageInFocus: null,
    activeTransaction: null,
    activeFilters: ["o"],
    showSequenceDiagram: false,
  };

  filters: { [x: string]: IFilter } = {
    o: {
      shortcut: "o",
      help: "'o' toggle option messages",
      activeText: "Hiding (o)ption messages",
    },
    t: {
      shortcut: "t",
      help: "'t' show transaction for message in focus",
      activeText: "Showing (t)ransaction",
      handler: () => {
        const { messageInFocus } = this.state;
        if (messageInFocus) {
          const transactionIdToShow = messageInFocus.msg.transactionId;
          const activeTransaction = this.state.activeTransaction === transactionIdToShow ? null : transactionIdToShow;
          this.setState({ activeTransaction });
        }
      },
    },
    c: {
      shortcut: "c",
      help: "'c' copy message in focus",
      flashMessage: "Copied message body to clipboard",
      handler: () => {
        const { messageInFocus } = this.state;
        clipboardCopy(JSON.stringify(messageInFocus?.msg, null, 2));
      },
    },
    m: {
      shortcut: "m",
      help: "'m' minimize/maximize",
    },
    r: {
      shortcut: "r",
      help: "'r' replay message",
      handler: () => {
        const { messageInFocus } = this.state;
        const savedMessageId = replayStorage.saveMessage(messageInFocus);
        window.open(`/interactive-bus?replay=${savedMessageId}`, "_blank");
      },
    },
    s: {
      shortcut: "s",
      help: "'s' sequence diagram",
      handler: () => {
        this.setState({ showSequenceDiagram: !this.state.showSequenceDiagram });
      },
    },
    f: {
      shortcut: "f",
      help: "'f' favorite message",
      handler: () => {
        const { messageInFocus } = this.state;
        const name =
          window.prompt("Enter a name", messageInFocus.subject) ||
          messageInFocus.subject + " " + messageInFocus.msg.reqId;
        libraryStorage.saveMessage(name, messageInFocus.subject, messageInFocus.msg);
      },
    },
  };

  async componentDidMount() {
    const { id, reqId } = this.props.match.params;
    const { data } = await apiClient.get<IMessageSequence>(`/recording/${id}/${reqId}`);
    this.setState({ messageSequence: data });
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("keyup", this.onKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("keyup", this.onKeyUp);
  }

  filterMessages() {
    const { messageSequence, activeTransaction, activeFilters } = this.state;

    if (!messageSequence) return [];
    else if (activeFilters.includes("t")) {
      return messageSequence.messages.filter((message) => message.msg.transactionId === activeTransaction);
    } else if (activeFilters.includes("o")) {
      return messageSequence.messages.filter((message) => !isOptionsMessage(message.subject));
    } else {
      return messageSequence.messages;
    }
  }

  onKeydown = (e: any) => {
    const tagName = document.activeElement ? document.activeElement.tagName : "";
    if (["input", "textarea"].includes(tagName.toLowerCase())) {
      return; // skip shortcut if user is editing a textarea or input
    }

    if (MODIFIER_KEYS.includes(e.key)) {
      this.isCommandOrControlKeyActive = true;
    }
    if (!this.isCommandOrControlKeyActive) {
      this.doShortcut(e.key as Shortcuts);
    }
  };

  onKeyUp = (e: any) => {
    if (MODIFIER_KEYS.includes(e.key)) {
      this.isCommandOrControlKeyActive = false;
    }
  };

  doShortcut(shortcut: Shortcuts) {
    const filter = this.filters[shortcut];

    if (filter) {
      if (this.state.activeFilters.includes(shortcut)) {
        this.setState({ activeFilters: this.state.activeFilters.filter((f) => f !== shortcut) });
      } else {
        this.setState({ activeFilters: [...this.state.activeFilters, shortcut] });
      }
      if (filter.flashMessage) {
        FlashControl.showSuccess(filter.flashMessage);
      }
      if (filter.handler) {
        filter.handler();
      }
    }
  }

  render() {
    const { messageSequence, activeFilters } = this.state;

    const title = messageSequence
      ? messageSequence.entrySubject + (messageSequence.user ? ", " + messageSequence.user : "")
      : "";

    return (
      <StyledMessageSequence>
        <RecordingStatusBar loading={false} query={title} fixedPosition={false} />

        {this.state.showSequenceDiagram ? this.renderSequenceDiagram() : this.renderMessages()}

        <ActiveFilter
          filters={activeFilters
            .filter((filter) => this.filters[filter].activeText)
            .map((filter) => this.filters[filter].activeText!)}
        />
        <Help rows={Object.values(this.filters).map((filter) => filter.help)} />
      </StyledMessageSequence>
    );
  }

  renderMessages() {
    const isExpanded = this.state.activeFilters.includes("m");
    return (
      <div className="messages">
        {this.filterMessages().map((message) => (
          <MessageRow
            key={`${message.subject}_${message.msg.transactionId}_${
              message.msg.from ? message.msg.from.instanceId : ""
            }`}
            message={message}
            expanded={isExpanded}
            onMouseEnter={(message) => this.setState({ messageInFocus: message })}
            onCopy={() => this.doShortcut("c")}
            onShowTransaction={() => this.doShortcut("t")}
            onReplay={() => this.doShortcut("r")}
            onFavorite={() => this.doShortcut("f")}
          />
        ))}
      </div>
    );
  }

  renderSequenceDiagram() {
    return (
      <div className="seq-diagram">
        <SequenceDiagram messages={this.filterMessages()} />
      </div>
    );
  }
}

export default MessageSequence;
